import { Close as CloseIcon, FavoriteRounded, Menu as MenuIcon } from '@mui/icons-material'
import {
    Box,
    Button,
    Drawer,
    Fade,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import me from '@/constants/me.ts'

const NavigationContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: '50%',
    right: theme.spacing(3),
    transform: 'translateY(-50%)',
    zIndex: 1100,
    transition: 'all 0.3s ease-in-out',
    [theme.breakpoints.down('md')]: {
        top: theme.spacing(2),
        left: theme.spacing(2),
        right: theme.spacing(2),
        transform: 'none',
        width: 'auto',
    },
}))

const NavWrapper = styled(Box)(({ theme }) => ({
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    borderRadius: '30px',
    padding: theme.spacing(2, 1),
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
    },
    [theme.breakpoints.down('md')]: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderRadius: '25px',
        padding: theme.spacing(1, 2),
    },
}))

const Logo = styled('img')(({ theme }) => ({
    width: 40,
    height: 40,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderRadius: '50%',
    border: '2px solid rgba(0, 173, 181, 0.3)',
    boxShadow: '0 4px 15px rgba(0, 173, 181, 0.2)',
    '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: '0 8px 25px rgba(0, 173, 181, 0.3)',
    },
    [theme.breakpoints.down('md')]: {
        width: 36,
        height: 36,
    },
}))

const NavButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
    color: active ? theme.palette.background.default : theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'none',
    padding: theme.spacing(1),
    borderRadius: '50%',
    minWidth: 40,
    width: 40,
    height: 40,
    background: active
        ? `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.primary.light })`
        : 'transparent',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.primary.light })`,
        opacity: 0,
        transition: 'opacity 0.3s ease',
    },
    '&:hover': {
        color: theme.palette.background.default,
        transform: 'scale(1.1)',
        boxShadow: '0 8px 25px rgba(0, 173, 181, 0.4)',
        border: `1px solid ${ theme.palette.primary.main }`,
        '&::before': {
            opacity: 1,
        },
    },
    '& .MuiButton-text': {
        position: 'relative',
        zIndex: 1,
    },
}))

const NavTooltip = styled(Box)(({ theme }) => ({
    position: 'absolute',
    right: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(34, 40, 49, 0.95)',
    backdropFilter: 'blur(15px)',
    color: theme.palette.text.primary,
    padding: theme.spacing(1, 2),
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    marginRight: theme.spacing(1),
    opacity: 0,
    visibility: 'hidden',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(0, 173, 181, 0.3)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
    whiteSpace: 'nowrap',
    '&::after': {
        content: '""',
        position: 'absolute',
        left: '100%',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid rgba(34, 40, 49, 0.95)',
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
    },
}))

const VerticalNavButton = styled(Box)(() => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover .nav-tooltip': {
        opacity: 1,
        transform: 'translateX(-10px)',
    },
}))

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.primary,
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    width: 44,
    height: 44,
    transition: 'all 0.3s ease',
    position: 'relative',
    border: '1px solid rgba(0, 173, 181, 0.3)',
    animation: 'pulse 2s infinite',
    '@keyframes pulse': {
        '0%': {
            boxShadow: '0 0 0 0 rgba(0, 173, 181, 0.4)',
        },
        '70%': {
            boxShadow: '0 0 0 10px rgba(0, 173, 181, 0)',
        },
        '100%': {
            boxShadow: '0 0 0 0 rgba(0, 173, 181, 0)',
        },
    },
    '&:hover': {
        background: `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.primary.light })`,
        color: theme.palette.background.default,
        transform: 'scale(1.15) rotate(180deg)',
        animation: 'none',
        boxShadow: '0 8px 25px rgba(0, 173, 181, 0.5)',
    },
    '&:active': {
        transform: 'scale(1.05) rotate(180deg)',
    },
}))

const MobileDrawer = styled(Drawer)(() => ({
    '& .MuiDrawer-paper': {
        background: 'linear-gradient(135deg, rgba(34, 40, 49, 0.95) 0%, rgba(57, 62, 70, 0.95) 100%)',
        backdropFilter: 'blur(25px)',
        width: 320,
        padding: 0,
        border: 'none',
        borderLeft: '1px solid rgba(0, 173, 181, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
        radial-gradient(circle at 20% 30%, rgba(0, 173, 181, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 70%, rgba(238, 238, 238, 0.05) 0%, transparent 50%)
      `,
            pointerEvents: 'none',
        },
    },
}))

const MobileNavButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
    width: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(1.5),
    borderRadius: '25px',
    color: active ? theme.palette.background.default : theme.palette.text.primary,
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    position: 'relative',
    overflow: 'hidden',
    background: active
        ? `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.primary.light })`
        : 'rgba(255, 255, 255, 0.05)',
    border: active
        ? `1px solid ${ theme.palette.primary.main }`
        : '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: active ? 'translateX(8px) scale(1.02)' : 'translateX(0) scale(1)',
    boxShadow: active
        ? `0 8px 25px rgba(0, 173, 181, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)`
        : '0 2px 10px rgba(0, 0, 0, 0.1)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
        transition: 'left 0.6s ease',
    },
    '&:hover': {
        background: active
            ? `linear-gradient(45deg, ${ theme.palette.primary.light }, ${ theme.palette.primary.main })`
            : `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.primary.light })`,
        color: theme.palette.background.default,
        transform: 'translateX(12px) scale(1.05)',
        boxShadow: `0 12px 35px rgba(0, 173, 181, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
        '&::before': {
            left: '100%',
        },
    },
    '&:active': {
        transform: 'translateX(10px) scale(0.98)',
    },
}))

const Navigation: React.FC = () => {
    const [ activeSection, setActiveSection ] = useState('hero')
    const [ mobileOpen, setMobileOpen ] = useState(false)
    const [ isVisible, setIsVisible ] = useState(false)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const currentYear = new Date().getFullYear()
    const navItems = [
        { name: 'Home', id: 'hero', short: 'H' },
        { name: 'About', id: 'about', short: 'A' },
        { name: 'Skills', id: 'skills', short: 'S' },
        { name: 'Experience', id: 'experience', short: 'E' },
        { name: 'Projects', id: 'projects', short: 'P' },
        { name: 'Testimonials', id: 'testimonials', short: 'T' },
        { name: 'Contact', id: 'contact', short: 'C' },
    ]

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY

            // Show navigation when user starts scrolling (after 100px)
            if ( scrollPosition > 100 ) {
                setIsVisible(true)
            } else {
                setIsVisible(false)
            }

            // Track active section
            const sections = navItems.map(item => document.getElementById(item.id))
            const scrollPositionWithOffset = scrollPosition + 150

            // Check if we're at the bottom of the page (for contact section)
            const isAtBottom = window.innerHeight + scrollPosition >= document.documentElement.scrollHeight - 50

            if ( isAtBottom ) {
                setActiveSection('contact')
                return
            }

            for ( let i = sections.length - 1; i >= 0; i-- ) {
                const section = sections[i]
                if ( section && section.offsetTop <= scrollPositionWithOffset ) {
                    setActiveSection(navItems[i].id)
                    break
                }
            }
        }

        // Add scroll listener
        window.addEventListener('scroll', handleScroll)

        // Initial check
        handleScroll()

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen)
    }

    const handleNavClick = (sectionId: string) => {
        setMobileOpen(false)
        const element = document.getElementById(sectionId)

        if ( element ) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
    }

    const MobileDoodle = styled(Box)(({ theme }) => ({
        position: 'absolute',
        fontSize: '1.2rem',
        opacity: 0.15,
        color: theme.palette.primary.main,
        pointerEvents: 'none',
        animation: 'float 3s ease-in-out infinite',
        '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-10px) rotate(5deg)' },
        },
    }))

    const doodles = [
        { symbol: '{ }', top: '15%', left: '10%', delay: '0s' },
        { symbol: '</>', top: '25%', right: '15%', delay: '1s' },
        { symbol: 'API', top: '45%', left: '8%', delay: '2s' },
        { symbol: '⚡', top: '65%', right: '12%', delay: '0.5s' },
        { symbol: 'JS', top: '75%', left: '15%', delay: '1.5s' },
        { symbol: '🚀', top: '85%', right: '20%', delay: '2.5s' },
    ]

    const drawer = (
        <Box sx={ { height: '100%', position: 'relative', overflow: 'hidden' } }>
            {/* Animated Doodles */ }
            { doodles.map((doodle, index) => (
                <MobileDoodle
                    key={ index }
                    sx={ {
                        top: doodle.top,
                        left: doodle.left,
                        right: doodle.right,
                        animationDelay: doodle.delay,
                    } }
                >
                    { doodle.symbol }
                </MobileDoodle>
            )) }

            {/* Header */ }
            <Box sx={ {
                p: 3,
                pb: 2,
                borderBottom: '1px solid rgba(0, 173, 181, 0.1)',
                background: 'rgba(0, 173, 181, 0.05)',
                position: 'relative',
                zIndex: 2,
            } }>
                <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
                    <Box sx={ { display: 'flex', alignItems: 'center', gap: 2 } }>
                        <Box sx={ {
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            border: '2px solid rgba(0, 173, 181, 0.3)',
                            boxShadow: '0 4px 15px rgba(0, 173, 181, 0.3)',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        } }>
                            <img
                                src="/tr_logo.png"
                                alt="Tejas Rana Logo"
                                style={ { width: '100%', height: '100%', objectFit: 'cover' } }
                            />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={ { fontWeight: 700, color: 'text.primary', lineHeight: 1 } }>
                                Tejas Rana
                            </Typography>
                            <Typography variant="caption" sx={ { color: 'text.secondary', fontSize: '0.8rem' } }>
                                Full Stack Developer
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={ handleDrawerToggle }
                        sx={ {
                            color: 'text.primary',
                            background: 'rgba(255, 255, 255, 0.1)',
                            width: 36,
                            height: 36,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.primary.light })`,
                                color: 'white',
                                transform: 'rotate(90deg) scale(1.1)',
                            },
                        } }
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Navigation Items */ }
            <Box sx={ { p: 3, pt: 4, position: 'relative', zIndex: 2 } }>
                <List sx={ { p: 0 } }>
                    { navItems.map((item, index) => (
                        <ListItem key={ item.name } disablePadding>
                            <Box
                                sx={ {
                                    width: '100%',
                                    opacity: 0,
                                    animation: `slideIn 0.5s ease forwards`,
                                    animationDelay: `${ index * 0.1 }s`,
                                    '@keyframes slideIn': {
                                        from: { opacity: 0, transform: 'translateX(-20px)' },
                                        to: { opacity: 1, transform: 'translateX(0)' },
                                    },
                                } }
                            >
                                <MobileNavButton
                                    onClick={ () => handleNavClick(item.id) }
                                    active={ activeSection === item.id }
                                >
                                    <Box sx={ { display: 'flex', alignItems: 'center', gap: 2 } }>
                                        <Box sx={ {
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: activeSection === item.id
                                                ? 'white'
                                                : `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.primary.light })`,
                                            transition: 'all 0.3s ease',
                                        } } />
                                        <ListItemText
                                            primary={ item.name }
                                            sx={ {
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: 600,
                                                    fontSize: '1.1rem',
                                                },
                                            } }
                                        />
                                    </Box>
                                </MobileNavButton>
                            </Box>
                        </ListItem>
                    )) }
                </List>
            </Box>

            {/* Footer */ }
            <Box sx={ {
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 3,
                background: 'rgba(0, 173, 181, 0.05)',
                borderTop: '1px solid rgba(0, 173, 181, 0.1)',
                zIndex: 2,
            } }>
                <Typography
                    variant="body2"
                    sx={ {
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    } }
                >
                    © { currentYear } { me.name }. Made with
                    <FavoriteRounded sx={ { color: 'primary.main', fontSize: '1rem' } } />
                </Typography>
            </Box>
        </Box>
    )

    return (
        <>
            <Fade in={ isVisible } timeout={ 500 }>
                <NavigationContainer sx={ {
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(20px)',
                    transition: 'all 0.3s ease-in-out',
                    [theme.breakpoints.down('md')]: {
                        transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
                    },
                } }>
                    <NavWrapper>
                        <Logo
                            src="/tr_logo.png"
                            alt="Tejas Rana Logo"
                            onClick={ () => handleNavClick('hero') }
                        />

                        { isMobile ? (
                            <MobileMenuButton
                                aria-label="open drawer"
                                onClick={ handleDrawerToggle }
                            >
                                <MenuIcon />
                            </MobileMenuButton>
                        ) : (
                            <Box sx={ { display: 'flex', flexDirection: 'column', gap: 1 } }>
                                { navItems.map((item, index) => (
                                    <Fade in={ isVisible } timeout={ 500 + index * 50 } key={ item.name }>
                                        <VerticalNavButton>
                                            <NavButton
                                                onClick={ () => handleNavClick(item.id) }
                                                active={ activeSection === item.id }
                                            >
                                                { item.short }
                                            </NavButton>
                                            <NavTooltip className="nav-tooltip">
                                                { item.name }
                                            </NavTooltip>
                                        </VerticalNavButton>
                                    </Fade>
                                )) }
                            </Box>
                        ) }
                    </NavWrapper>
                </NavigationContainer>
            </Fade>

            <MobileDrawer
                variant="temporary"
                anchor="right"
                open={ mobileOpen }
                onClose={ handleDrawerToggle }
                ModalProps={ {
                    keepMounted: true,
                } }
            >
                { drawer }
            </MobileDrawer>
        </>
    )
}

export default Navigation
